/**
 * 增强的菜单图标转换工具
 * 提供更全面的图标支持和更好的容错处理
 */

import * as Icons from '@ant-design/icons';
import React from 'react';

// 常见图标名称映射表
const ICON_NAME_MAPPING: Record<string, string> = {
  // 基础图标
  home: 'HomeOutlined',
  dashboard: 'DashboardOutlined',
  setting: 'SettingOutlined',
  settings: 'SettingOutlined',
  config: 'SettingOutlined',
  manage: 'SettingOutlined',
  admin: 'SettingOutlined',

  // 用户相关
  user: 'UserOutlined',
  users: 'UserOutlined',
  person: 'UserOutlined',
  people: 'UserOutlined',
  profile: 'UserOutlined',
  account: 'UserOutlined',
  login: 'LoginOutlined',
  logout: 'LogoutOutlined',

  // 组织架构
  organization: 'ApartmentOutlined',
  org: 'ApartmentOutlined',
  company: 'ApartmentOutlined',
  business: 'ApartmentOutlined',
  enterprise: 'ApartmentOutlined',
  department: 'TeamOutlined',
  dept: 'TeamOutlined',
  team: 'TeamOutlined',
  group: 'TeamOutlined',
  position: 'IdcardOutlined',
  post: 'IdcardOutlined',
  job: 'IdcardOutlined',
  role: 'IdcardOutlined',
  employee: 'UserOutlined',
  staff: 'UserOutlined',
  worker: 'UserOutlined',

  // 系统管理
  system: 'SettingOutlined',
  sys: 'SettingOutlined',
  menu: 'MenuOutlined',
  navigation: 'MenuOutlined',
  dict: 'BookOutlined',
  dictionary: 'BookOutlined',
  book: 'BookOutlined',
  permission: 'SafetyCertificateOutlined',
  security: 'SafetyCertificateOutlined',
  safe: 'SafetyCertificateOutlined',

  // 设备管理
  device: 'DesktopOutlined',
  devices: 'DesktopOutlined',
  equipment: 'DesktopOutlined',
  hardware: 'DesktopOutlined',
  model: 'DatabaseOutlined',
  models: 'DatabaseOutlined',
  supplier: 'TeamOutlined',
  vendor: 'TeamOutlined',
  gateway: 'GatewayOutlined',
  gateways: 'GatewayOutlined',
  terminal: 'DesktopOutlined',
  terminals: 'DesktopOutlined',

  // 事件管理
  event: 'AlertOutlined',
  events: 'AlertOutlined',
  alert: 'AlertOutlined',
  notification: 'BellOutlined',
  notice: 'BellOutlined',
  record: 'DatabaseOutlined',
  records: 'DatabaseOutlined',
  history: 'HistoryOutlined',
  log: 'HistoryOutlined',
  order: 'FileTextOutlined',
  orders: 'FileTextOutlined',
  ticket: 'FileTextOutlined',
  node: 'NodeIndexOutlined',
  nodes: 'NodeIndexOutlined',
  receiver: 'BellOutlined',
  receivers: 'BellOutlined',
  resource: 'FileOutlined',
  resources: 'FileOutlined',
  file: 'FileOutlined',
  files: 'FileOutlined',

  // 套餐管理
  package: 'GiftOutlined',
  packages: 'GiftOutlined',
  bundle: 'GiftOutlined',
  subscription: 'TagsOutlined',
  subscriptions: 'TagsOutlined',
  tag: 'TagsOutlined',
  tags: 'TagsOutlined',
  my: 'GiftOutlined',
  personal: 'GiftOutlined',
  sub: 'TagsOutlined',
  child: 'TagsOutlined',

  // 其他常用图标
  list: 'UnorderedListOutlined',
  table: 'TableOutlined',
  form: 'FormOutlined',
  edit: 'EditOutlined',
  delete: 'DeleteOutlined',
  add: 'PlusOutlined',
  create: 'PlusOutlined',
  save: 'SaveOutlined',
  search: 'SearchOutlined',
  filter: 'FilterOutlined',
  sort: 'SortAscendingOutlined',
  download: 'DownloadOutlined',
  upload: 'UploadOutlined',
  import: 'ImportOutlined',
  export: 'ExportOutlined',
  print: 'PrinterOutlined',
  share: 'ShareAltOutlined',
  link: 'LinkOutlined',
  unlink: 'DisconnectOutlined',
  refresh: 'ReloadOutlined',
  reset: 'UndoOutlined',
  help: 'QuestionCircleOutlined',
  info: 'InfoCircleOutlined',
  success: 'CheckCircleOutlined',
  error: 'CloseCircleOutlined',
  warning: 'ExclamationCircleOutlined',
  cloud: 'CloudOutlined',
  server: 'CloudServerOutlined',
  database: 'DatabaseOutlined',
  api: 'ApiOutlined',
  code: 'CodeOutlined',
  mobile: 'MobileOutlined',
  phone: 'PhoneOutlined',
  email: 'MailOutlined',
  mail: 'MailOutlined',
  calendar: 'CalendarOutlined',
  time: 'ClockCircleOutlined',
  clock: 'ClockCircleOutlined',
  location: 'EnvironmentOutlined',
  map: 'EnvironmentOutlined',
  star: 'StarOutlined',
  heart: 'HeartOutlined',
  like: 'LikeOutlined',
  dislike: 'DislikeOutlined',
  eye: 'EyeOutlined',
  view: 'EyeOutlined',
  hide: 'EyeInvisibleOutlined',
  lock: 'LockOutlined',
  unlock: 'UnlockOutlined',
  key: 'KeyOutlined',
  password: 'LockOutlined',
  image: 'PictureOutlined',
  photo: 'PictureOutlined',
  picture: 'PictureOutlined',
  video: 'PlaySquareOutlined',
  audio: 'SoundOutlined',
  music: 'SoundOutlined',
  document: 'FileTextOutlined',
  pdf: 'FilePdfOutlined',
  excel: 'FileExcelOutlined',
  word: 'FileWordOutlined',
  power: 'PoweroffOutlined',
  shutdown: 'PoweroffOutlined',
  restart: 'RedoOutlined',
  backup: 'CopyOutlined',
  copy: 'CopyOutlined',
  paste: 'SnippetsOutlined',
  cut: 'ScissorOutlined',
  fullscreen: 'FullscreenOutlined',
  exit: 'FullscreenExitOutlined',
  zoom: 'ZoomInOutlined',
  'zoom-out': 'ZoomOutOutlined',
  move: 'DragOutlined',
  drag: 'DragOutlined',
  select: 'SelectOutlined',
  check: 'CheckOutlined',
  close: 'CloseOutlined',
  cancel: 'CloseOutlined',
  confirm: 'CheckOutlined',
  ok: 'CheckOutlined',
  yes: 'CheckOutlined',
  no: 'CloseOutlined',
};

/**
 * 增强的图标转换函数
 * @param iconName 图标名称
 * @returns React图标组件或undefined
 */
export function enhancedIconConverter(
  iconName: string | undefined,
): React.ReactNode {
  if (!iconName || typeof iconName !== 'string') {
    // 图标名称为空或不是字符串
    return undefined;
  }

  // 清理输入（保留原始大小写，因为Ant Design图标名是首字母大写的）
  const cleanName = iconName.replace(/['"]/g, '').trim();
  const lowerName = cleanName.toLowerCase();

  // 1. 首先尝试直接匹配Ant Design图标组件（使用原始大小写）
  if (cleanName in Icons) {
    const IconComponent = Icons[cleanName as keyof typeof Icons];
    return React.createElement(IconComponent as any);
  }

  // 2. 尝试添加Outlined后缀（如果还没有）
  const outlinedName = cleanName.endsWith('Outlined')
    ? cleanName
    : `${cleanName}Outlined`;
  if (outlinedName in Icons) {
    const IconComponent = Icons[outlinedName as keyof typeof Icons];
    return React.createElement(IconComponent as any);
  }

  // 3. 使用名称映射表（使用小写匹配）
  if (ICON_NAME_MAPPING[lowerName]) {
    const mappedName = ICON_NAME_MAPPING[lowerName];
    if (mappedName in Icons) {
      const IconComponent = Icons[mappedName as keyof typeof Icons];
      return React.createElement(IconComponent as any);
    }
  }

  // 4. 尝试部分匹配（模糊匹配，使用小写）
  const partialMatch = Object.keys(ICON_NAME_MAPPING).find(
    (key) => lowerName.includes(key) || key.includes(lowerName),
  );

  if (partialMatch && ICON_NAME_MAPPING[partialMatch]) {
    const mappedName = ICON_NAME_MAPPING[partialMatch];
    if (mappedName in Icons) {
      const IconComponent = Icons[mappedName as keyof typeof Icons];
      return React.createElement(IconComponent as any);
    }
  }

  // 5. 尝试常见的命名变体
  const variants = [
    cleanName,
    `${cleanName}Outlined`,
    `${cleanName}Filled`,
    `${cleanName}TwoTone`,
    `${cleanName.charAt(0).toUpperCase()}${cleanName.slice(1)}Outlined`,
    `${cleanName.replace(/[-_\s]/g, '')}Outlined`, // 移除分隔符
  ];

  for (const variant of variants) {
    if (variant in Icons) {
      const IconComponent = Icons[variant as keyof typeof Icons];
      return React.createElement(IconComponent as any);
    }
  }

  // 未找到匹配，返回undefined让后续转换器尝试
  return undefined;
}

/**
 * 批量转换菜单图标
 * @param menus 菜单数组
 * @returns 转换后的菜单数组
 */
export function convertMenuIcons(menus: any[]): any[] {
  if (!menus || menus.length === 0) {
    return [];
  }

  return menus.map((menu) => {
    const convertedMenu = {
      ...menu,
      icon: enhancedIconConverter(menu.icon),
    };

    // 递归处理子菜单
    if (menu.children && menu.children.length > 0) {
      convertedMenu.children = convertMenuIcons(menu.children);
    }

    // 处理routes属性
    if (menu.routes && menu.routes.length > 0) {
      convertedMenu.routes = convertMenuIcons(menu.routes);
    }

    return convertedMenu;
  });
}

/**
 * 获取所有可用的图标名称列表（用于调试）
 * @returns 图标名称数组
 */
export function getAvailableIcons(): string[] {
  return Object.keys(Icons)
    .filter(
      (key) =>
        key.endsWith('Outlined') ||
        key.endsWith('Filled') ||
        key.endsWith('TwoTone'),
    )
    .sort();
}
